import { apiClient, ApiResponse } from "./client";

// ============================================================================
// Types
// ============================================================================

export type ProductStatus = "draft" | "active" | "archived" | "discontinued";
export type PricingType = "one_time" | "subscription";
export type BillingPeriod = "monthly" | "quarterly" | "yearly" | "lifetime";

export interface PricingResponse {
    id: number;
    name: string;
    pricingType: PricingType;
    billingPeriod: BillingPeriod;
    billingPeriodMonths: number | null;
    price: number;
    currency: string;
    originalPrice: number | null;
    discountPercentage: number | null;
    maxSeats: number;
    hasTrial: boolean;
    trialDays: number;
    isDefault: boolean;
    isNegotiable: boolean;
}

export interface ProductContentResponse {
    contentId: number;
    title: string;
    customTitle: string | null;
    sequenceOrder: number;
    shortDescription: string | null;
    learningObjectives: string | null;
    concepts: string | null;
    lessonCount: number;
    estimatedHours: number;
}

export interface ProductResponse {
    id: number;
    name: string;
    slug: string | null;
    shortDescription: string | null;
    description: string | null;
    thumbnailUrl: string | null;
    bannerUrl: string | null;
    status: ProductStatus;
    linkedContentId: number | null;
    curriculumTitle: string | null;
    contents: ProductContentResponse[];
    pricingOptions: PricingResponse[];
    createdAt: string | null;
    isBundle: boolean;
}

export interface CreateOrderRequest {
    productId: number;
    pricingId: number;
    quantity?: number;
    seats?: number; // 선택한 인원수 (undefined면 pricing.maxSeats 사용, 0이면 무제한)
}

export interface OrderResponse {
    id: number;
    orderNumber: string;
    orderStatus: string;
    subtotal: number;
    discountAmount: number;
    taxAmount: number;
    totalAmount: number;
    currency: string;
    createdAt: string | null;
}

export interface ProcessPaymentRequest {
    orderId: number;
    paymentMethod: string;
    cardNumber?: string;
    validFrom?: string; // B2B 라이선스 시작일 (ISO format: yyyy-MM-dd)
}

export interface PaymentResponse {
    order: OrderResponse;
    payment: {
        id: number;
        paymentStatus: string;
        pgTransactionId: string;
    };
    subscriptionId?: number;
    message: string;
}

// ============================================================================
// API Functions
// ============================================================================

export const commerceApi = {
    /**
     * 모든 상품 목록 조회 (구매 가능한 상품)
     */
    getProducts: async (): Promise<ProductResponse[]> => {
        const response = await apiClient.get<ApiResponse<ProductResponse[]>>(
            "/commerce/products",
        );
        return response.data.data || [];
    },

    /**
     * 상품 상세 조회
     */
    getProduct: async (productId: number): Promise<ProductResponse> => {
        const response = await apiClient.get<ApiResponse<ProductResponse>>(
            `/commerce/products/${productId}`,
        );
        if (!response.data.data) {
            throw new Error("Product not found");
        }
        return response.data.data;
    },

    /**
     * 주문 생성
     */
    createOrder: async (
        request: CreateOrderRequest,
    ): Promise<OrderResponse> => {
        const response = await apiClient.post<ApiResponse<OrderResponse>>(
            "/commerce/orders",
            request,
        );
        if (!response.data.data) {
            throw new Error("Failed to create order");
        }
        return response.data.data;
    },

    /**
     * 결제 처리
     */
    processPayment: async (
        request: ProcessPaymentRequest,
    ): Promise<PaymentResponse> => {
        const response = await apiClient.post<ApiResponse<PaymentResponse>>(
            "/commerce/orders/pay",
            request,
        );
        if (!response.data.data) {
            throw new Error("Failed to process payment");
        }
        return response.data.data;
    },
};
